/**
 * 单位换算 —— 全部是纯函数，不持有状态、不做 I/O。
 *
 * 三条设计取舍，值得先说清楚：
 *
 * 1. **每个换算都是一对方向明确的函数**（`mpsToCms` / `cmsToMps`），不做
 *    "靠参数决定方向"的通用函数。`convert(value, from, to)` 这种签名读起来必须
 *    记住参数顺序，而单位搞反不会报错，只会让飞机飞错高度。名字里带方向就没有
 *    这种可能性。
 *
 * 2. **经纬度内部一律用 ×1e7 定点整数**。原因：MAVLink 的 `lat`/`lon` 字段本身
 *    就是 `int32` 的"度 × 1e7"，而 JS 的 `number` 是 float64、只在实际存成
 *    `float32` 时才丢精度。1e-7 度 ≈ 1.1 cm（纬度方向），分辨率足够；反过来若
 *    把十进制度塞进 float32，24 位有效位在 ±180 上只能分辨约 1.5e-5 度 ≈ 1.7 m，
 *    纬度、经度分别丢到米级 —— 围栏顶点和备降点会因此在图上肉眼可见地漂。
 *    代价是每处跨界都要显式换算，这正是本文件存在的理由。
 *
 * 3. **不做量纲推导**。不引入 "Quantity" 类型系统；本项目里单位出现在每个标识符
 *    后缀（`altM`、`speedMps`、`headingDeg`）里，靠命名而不是靠类型机检 —— 简单、
 *    零运行时开销，代价是命名纪律必须靠 review 守住。
 */

/** 每弧度多少度。 */
export const DEG_PER_RAD = 180 / Math.PI;

/** 每度多少弧度。 */
export const RAD_PER_DEG = Math.PI / 180;

export function degToRad(deg: number): number {
  return deg * RAD_PER_DEG;
}

export function radToDeg(rad: number): number {
  return rad * DEG_PER_RAD;
}

/* ────────────────────────────── 角度 ────────────────────────────── */

/**
 * 把角度规整到 [0, 360)。
 *
 * `%` 在 JS 里对负数返回负值（`-10 % 360 === -10`），所以必须再补一次 `+360`。
 * 输入是 NaN 时返回 NaN —— 静默返回 0 会让"没有航向"看起来像"朝正北"。
 */
export function normalizeHeadingDeg(deg: number): number {
  if (!Number.isFinite(deg)) return NaN;
  const wrapped = deg % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

/** 把经度规整到 [-180, 180)。与 `normalizeHeadingDeg` 同理，负余数要补。 */
export function normalizeLongitudeDeg(lonDeg: number): number {
  if (!Number.isFinite(lonDeg)) return NaN;
  // 先把范围收进 [-360, 360)，一次加减即可解决 |lon| < 540 以外的极端值循环
  let wrapped = ((lonDeg % 360) + 360) % 360; // [0, 360)
  if (wrapped >= 180) wrapped -= 360;
  return wrapped;
}

/**
 * 两个航向之间的最小夹角，结果在 (-180, 180]。
 * 正数表示 `b` 在 `a` 的顺时针方向（航向角顺时针为正）。
 */
export function headingDifferenceDeg(aDeg: number, bDeg: number): number {
  const diff = ((bDeg - aDeg) % 360 + 540) % 360 - 180;
  return diff === -180 ? 180 : diff;
}

/**
 * 最短转向方向。返回 -1（逆时针）、0（已对齐）或 +1（顺时针）。
 *
 * `toleranceDeg` 用来避免"目标航向差 0.0001°，飞机原地左右抖"：
 * 阈值以内视为已对齐。
 */
export function shortestTurnDirection(fromDeg: number, toDeg: number, toleranceDeg = 0): 0 | 1 | -1 {
  const diff = headingDifferenceDeg(fromDeg, toDeg);
  if (Math.abs(diff) <= Math.abs(toleranceDeg)) return 0;
  return diff > 0 ? 1 : -1;
}

/* ───────────────────── 经纬度：十进制 ↔ ×1e7 整数 ───────────────────── */

/** MAVLink 经纬度定标：1 度 = 1e7 个计数。 */
export const LATLON_E7_PER_DEG = 1e7;

/**
 * MAVLink 的 `int32` 经纬度（度 × 1e7）→ 十进制度。
 *
 * 这是**精确**的：1e-7 度本身在 float64 里表示不了，但除法产生的相对误差约
 * 1 ulp（≈ 2.2e-16），换算回米是 1e-9 mm 量级，远小于 GPS 本身的分辨率。
 */
export function latLonE7ToDeg(valueE7: number): number {
  return valueE7 / LATLON_E7_PER_DEG;
}

/**
 * 十进制度 → MAVLink 的 `int32` 经纬度。
 *
 * 用 `Math.round` 而不是 `Math.trunc`：`Math.trunc(-0.9e-7) === 0`，
 * 会让极小的负值全部塌到 0（赤道/本初子午线附近出现一条"吸附带"）。
 *
 * `+ 0` 是为了把 `Math.round(-0.2)` 产生的 `-0` 归一成 `0`：
 * `-0` 与 `0` 在 `===` 下相等，但 `Object.is` 与 `JSON.stringify` 下不同，
 * 会在日志和快照测试里制造无意义的差异。
 */
export function degToLatLonE7(deg: number): number {
  return Math.round(deg * LATLON_E7_PER_DEG) + 0;
}

/**
 * ×1e7 整数往返无损性的边界。
 *
 * `degToLatLonE7(latLonE7ToDeg(n)) === n` 对所有 `|n| <= 2^53 / 1e7` 成立 ——
 * 也就是说远超 `int32` 的 ±2.147e9（±214.7 度）。超过这个范围（约 ±9.0e8 度）
 * 才开始丢，而 `int32` 早就溢出了，所以实践中"无损"是无条件的。
 */
export const LATLON_E7_SAFE_RANGE = Number.MAX_SAFE_INTEGER / LATLON_E7_PER_DEG;

/* ────────────────────────────── 速度 ────────────────────────────── */

/** m/s → cm/s。MAVLink 的 `*_CM_S` 字段用厘米每秒。 */
export function mpsToCms(mps: number): number {
  return mps * 100;
}

export function cmsToMps(cms: number): number {
  return cms / 100;
}

/** m/s → km/h。界面上的地速习惯用 km/h（中文飞手习惯，1 m/s = 3.6 km/h）。 */
export function mpsToKph(mps: number): number {
  return mps * 3.6;
}

export function kphToMps(kph: number): number {
  return kph / 3.6;
}

/** m/s → kn（节）。1 kn = 1.852 km/h = 0.514444… m/s，用于风速与固定翼空速。 */
export const MPS_PER_KNOT = 1852 / 3600;

export function mpsToKnots(mps: number): number {
  return mps / MPS_PER_KNOT;
}

export function knotsToMps(knots: number): number {
  return knots * MPS_PER_KNOT;
}

/* ────────────────────────────── 长度 ────────────────────────────── */

/**
 * m → mm。MAVLink 的 `GLOBAL_POSITION_INT.relative_alt`、`VFR_HUD.alt` 之外的
 * 多处高度字段用毫米。
 *
 * 注意 `GLOBAL_POSITION_INT.alt` 是 `int32` 毫米：能表示的绝对值上限是约
 * 2147483 m。日常飞行够用，但把"海拔 + 地球半径"之类的大数塞进去会溢出，
 * 调用方需要自己保证输入是合理范围内的**高度**而不是距离。
 */
export function mToMm(m: number): number {
  return m * 1000;
}

export function mmToM(mm: number): number {
  return mm / 1000;
}

/** m → cm。`*_CM` 的距离字段（如测距仪、围栏半径）用厘米。 */
export function mToCm(m: number): number {
  return m * 100;
}

export function cmToM(cm: number): number {
  return cm / 100;
}

/* ─────────────────── 电池 / 电气 / 环境（遥测常见） ─────────────────── */

/** V → mV。`SYS_STATUS.voltage_battery` 用毫伏。 */
export function vToMv(volts: number): number {
  return volts * 1000;
}

export function mvToV(millivolts: number): number {
  return millivolts / 1000;
}

/** A → cA。`SYS_STATUS.current_battery` 用 10 mA（厘安）。 */
export function aToCa(amps: number): number {
  return amps * 100;
}

export function cAToA(centiamps: number): number {
  return centiamps / 100;
}

/** `SYS_STATUS.battery_remaining` 的哨兵值：-1 表示"电量未知"。 */
export const BATTERY_REMAINING_UNKNOWN = -1;

/**
 * `battery_remaining`（0..100，-1 表示未知）→ 0..100 的百分比，未知返回 `undefined`。
 *
 * 返回 `undefined` 而不是 0 或 -1：界面必须能区分"0% 要没电了"和"不知道"。
 */
export function batteryRemainingPercent(raw: number): number | undefined {
  if (raw < 0 || raw > 100) return undefined;
  return raw;
}

/** 摄氏度（×100）→ 摄氏度。ArduPilot 的温度遥测用 cdegC。 */
export function cdegCToDegC(centiDegC: number): number {
  return centiDegC / 100;
}

export function degCToCdegC(degC: number): number {
  return Math.round(degC * 100) + 0;
}

/** Pa → hPa。`SCALED_PRESSURE.press_abs` 用百帕。 */
export function paToHpa(pascals: number): number {
  return pascals / 100;
}

export function hpaToPa(hectopascals: number): number {
  return hectopascals * 100;
}

/* ────────────────────────────── 时间 ────────────────────────────── */

export function usToMs(microseconds: number): number {
  return microseconds / 1000;
}

export function msToUs(milliseconds: number): number {
  return milliseconds * 1000;
}

export function msToSec(milliseconds: number): number {
  return milliseconds / 1000;
}

export function secToMs(seconds: number): number {
  return seconds * 1000;
}

export function secToUs(seconds: number): number {
  return seconds * 1e6;
}

/**
 * 采样率 → 采样间隔（微秒）。用于 `MAV_CMD_SET_MESSAGE_INTERVAL`。
 *
 * 非正数（0 Hz、负 Hz 都是无意义输入）返回 `undefined` 而不是 `Infinity`：
 * 把 `Infinity` 发给飞控会变成一个巨大的整数，飞控要么拒绝要么真的停止发送，
 * 两种结果都不是调用方想要的。
 */
export function hzToIntervalUs(hz: number): number | undefined {
  if (!(hz > 0) || !Number.isFinite(hz)) return undefined;
  return Math.round(1e6 / hz);
}

/** 距离 / 速度 → 预计耗时（秒）。速度非正时返回 `undefined`（无法到达）。 */
export function travelTimeSec(distanceM: number, speedMps: number): number | undefined {
  if (!(speedMps > 0) || !Number.isFinite(speedMps)) return undefined;
  return distanceM / speedMps;
}

/* ──────────────────────────── 数值小工具 ──────────────────────────── */

export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return value;
  return value < min ? min : value > max ? max : value;
}

/** 线性插值，`t` 不裁剪（调用方需要外推时不必绕路）。 */
export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}
